import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { ArrowLeft } from 'lucide-react';

const LegalNotice: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();
    const legal = t.legal || {};
    const content = legal.legalNoticeContent || {};

    useSEO({
        title: legal.seo?.legal?.title || "Aviso Legal - NexuCapital",
        description: legal.seo?.legal?.description || "Informacoes legais sobre a NexuCapital Empreendimentos LTDA.",
        path: '/legal-notice',
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        noindex: false,
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Legal', url: '/legal' },
                { name: 'Legal Notice', url: '/legal-notice' }
            ]),
            generateWebPageSchema({
                name: legal.seo?.legal?.title || "Aviso Legal - NexuCapital",
                description: legal.seo?.legal?.description || "Informacoes legais sobre a NexuCapital Empreendimentos LTDA.",
                url: '/legal-notice',
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>

            <section className="pt-24 pb-16 bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4">
                    {/* Back Link */}
                    <a
                        href="/"
                        className="inline-flex items-center text-primary hover:text-primary-dark mb-6 transition-colors"
                    >
                        <ArrowLeft size={18} className="mr-2" />
                        {t.navigation?.home || "Inicio"}
                    </a>

                    {/* Content Card */}
                    <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 md:p-12 max-w-4xl mx-auto">
                        {/* Header */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary mb-4 pb-4 border-b-2 border-secondary">
                            {legal.legalNotice || "Aviso Legal"}
                        </h1>

                        {/* Section 1 - Company Identification */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section1Title}
                        </h2>
                        <ul className="space-y-3 text-gray-700 mb-8">
                            <li className="flex flex-col sm:flex-row sm:gap-2">
                                <strong className="min-w-[160px]">Razao Social:</strong>
                                <span>{content.companyName}</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:gap-2">
                                <strong className="min-w-[160px]">Nome Fantasia:</strong>
                                <span>{content.tradeName}</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:gap-2">
                                <strong className="min-w-[160px]">CNPJ:</strong>
                                <span>{content.cnpj}</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:gap-2">
                                <strong className="min-w-[160px]">Endereco:</strong>
                                <span>{content.address}</span>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:gap-2">
                                <strong className="min-w-[160px]">Contato:</strong>
                                <a
                                    href={`mailto:${content.contact}`}
                                    className="text-primary hover:text-primary-dark underline transition-colors"
                                >
                                    {content.contact}
                                </a>
                            </li>
                            <li className="flex flex-col sm:flex-row sm:gap-2">
                                <strong className="min-w-[160px]">Registros:</strong>
                                <span>{content.registrations}</span>
                            </li>
                        </ul>

                        {/* Section 2 - Intellectual Property */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section2Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section2Text}
                        </p>

                        {/* Section 3 - Financial Disclaimer */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section3Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section3Text}
                        </p>
                        <ul className="list-disc pl-6 space-y-3 text-gray-700 mb-8">
                            {content.section3Items?.map((item: string, index: number) => (
                                <li key={index}>{item}</li>
                            ))}
                        </ul>

                        {/* Section 4 - Accuracy of Information */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section4Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section4Text}
                        </p>

                        {/* Section 5 - Third-Party Links */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section5Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section5Text}
                        </p>

                        {/* Section 6 - Limitation of Liability */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section6Title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section6Text}
                        </p>

                        {/* Link to Terms */}
                        <div className="mt-8 pt-6 border-t border-gray-200">
                            <p className="text-gray-700 leading-relaxed">
                                Para regras detalhadas sobre o uso da plataforma, consulte nossos{' '}
                                <a
                                    href="/terms-of-use"
                                    className="text-primary hover:text-primary-dark underline transition-colors"
                                >
                                    {legal.termsOfUse || "Termos de Uso"}
                                </a>.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
};

export default LegalNotice;
